import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, Lock } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'
import { useBackupDays, useUpdateBackupDays } from '@/hooks/useBackupDays'
import type { BackupDaysMode, BackupDaysRow } from '@/api/clt.types'

/** Shipping transit is system-managed; only Rail / Truck are editable. */
const SHIPPING_READONLY_HINT =
  'Shipping transit is system-managed and read-only. You can edit the Rail and Truck legs.'

const BACKUP_DAYS_MODES: {
  key: BackupDaysMode
  label: string
}[] = [
  { key: 'shipping', label: 'Shipping' },
  { key: 'rail', label: 'Rail' },
  { key: 'truck', label: 'Truck' },
]

interface BackupDaysMatrixTableProps {
  /** Tailwind max-height for the scroll area (dialog vs full page). */
  scrollClassName?: string
}

const DEBOUNCE_MS = 300

/**
 * The Shipping/Rail/Truck backup-days table. Shipping is read-only with a
 * tooltip; Rail and Truck are editable. Edits debounce → PUT one cell.
 */
export function BackupDaysMatrixTable({
  scrollClassName = 'max-h-[360px]',
}: BackupDaysMatrixTableProps) {
  const [query, setQuery] = useState('')
  const { data, isLoading, isError } = useBackupDays({ limit: 2000 })
  const updateMutation = useUpdateBackupDays()

  const rows = useMemo(() => data?.data ?? [], [data])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      row => row.origin.toLowerCase().includes(q) || String(row.wh).includes(q)
    )
  }, [rows, query])

  // Local draft values per (origin, wh, mode) so typing feels instant; we
  // debounce the PUT to one call per pause.
  const [drafts, setDrafts] = useState<Record<string, number>>({})
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const id of Object.values(timers)) clearTimeout(id)
    }
  }, [])

  const draftKey = (origin: string, wh: number, mode: BackupDaysMode) =>
    `${origin}|${wh}|${mode}`

  const onCellChange = (
    row: BackupDaysRow,
    mode: BackupDaysMode,
    raw: string
  ) => {
    const value = Number(raw)
    if (!Number.isFinite(value) || value < 0) return
    const key = draftKey(row.origin, row.wh, mode)
    setDrafts(d => ({ ...d, [key]: value }))
    if (timersRef.current[key]) clearTimeout(timersRef.current[key])
    timersRef.current[key] = setTimeout(() => {
      updateMutation.mutate(
        { origin: row.origin, wh: row.wh, mode, days: value },
        {
          onSettled: () => {
            setDrafts(d => {
              const next = { ...d }
              delete next[key]
              return next
            })
          },
        }
      )
    }, DEBOUNCE_MS)
  }

  const renderValue = (
    row: BackupDaysRow,
    mode: BackupDaysMode
  ): number | '' => {
    const key = draftKey(row.origin, row.wh, mode)
    if (key in drafts) return drafts[key]
    return row[mode] ?? ''
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter by origin or DC..."
          className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>

      <TooltipProvider delayDuration={150}>
        <div
          className={cn(
            'overflow-y-auto rounded-md border border-border',
            scrollClassName
          )}
        >
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-muted/60">
              <tr className="text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Origin</th>
                <th className="px-3 py-2 text-left font-medium">DC (wh)</th>
                {BACKUP_DAYS_MODES.map(mode => (
                  <th
                    key={mode.key}
                    className="px-3 py-2 text-right font-medium"
                  >
                    {mode.key === 'shipping' ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex cursor-help items-center justify-end gap-1">
                            {mode.label}
                            <Lock className="h-3 w-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px]">
                          {SHIPPING_READONLY_HINT}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      mode.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={2 + BACKUP_DAYS_MODES.length}
                    className="px-3 py-4 text-center text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td
                    colSpan={2 + BACKUP_DAYS_MODES.length}
                    className="px-3 py-4 text-center text-destructive"
                  >
                    Failed to load backup days.
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={2 + BACKUP_DAYS_MODES.length}
                    className="px-3 py-4 text-center text-muted-foreground"
                  >
                    No matching rows.
                  </td>
                </tr>
              ) : (
                filtered.map(row => (
                  <tr
                    key={`${row.origin}-${row.wh}`}
                    className="border-t border-border/60"
                  >
                    <td className="px-3 py-1.5 font-medium text-foreground">
                      {row.origin}
                    </td>
                    <td className="px-3 py-1.5 text-foreground">{row.wh}</td>
                    {BACKUP_DAYS_MODES.map(mode =>
                      mode.key === 'shipping' ? (
                        <td key={mode.key} className="px-2 py-1 text-right">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex h-8 w-16 cursor-not-allowed items-center justify-end rounded-md border border-transparent px-2 text-sm tabular-nums text-muted-foreground">
                                {row.shipping ?? '—'}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[220px]">
                              {SHIPPING_READONLY_HINT}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      ) : (
                        <td key={mode.key} className="px-2 py-1 text-right">
                          <input
                            type="number"
                            min={0}
                            value={renderValue(row, mode.key)}
                            onChange={e =>
                              onCellChange(row, mode.key, e.target.value)
                            }
                            className="h-8 w-16 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          />
                        </td>
                      )
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </TooltipProvider>

      <p className="text-xs text-muted-foreground">
        Showing {filtered.length} of {rows.length} rows. Rail and Truck are
        editable; Shipping is read-only.
      </p>
    </div>
  )
}
